import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Agency } from '@/types'

export const useAgency = (agencyId: string | null | undefined) => {
  const [agency, setAgency] = useState<Agency | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!agencyId) {
      setAgency(null)
      setLoading(false)
      return
    }

    const fetchAgency = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('agencies')
          .select('*')
          .eq('id', agencyId)
          .single()

        if (error) throw error
        setAgency(data)
        setError(null)
      } catch (err) {
        console.error('Error fetching agency:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch agency')
        setAgency(null)
      } finally {
        setLoading(false)
      }
    }

    fetchAgency()
  }, [agencyId])

  const updateAgency = async (updates: Partial<Agency>) => {
    if (!agencyId) return { error: 'No agency ID provided' }

    try {
      const { error } = await supabase
        .from('agencies')
        .update(updates)
        .eq('id', agencyId)

      if (error) throw error

      // Update local state
      setAgency((prev) => (prev ? { ...prev, ...updates } : null))
      return { error: null }
    } catch (err) {
      console.error('Error updating agency:', err)
      return { error: err instanceof Error ? err.message : 'Failed to update agency' }
    }
  }

  return { agency, loading, error, updateAgency }
}

export const useAgencyProjects = (agencyId: string | null | undefined) => {
  const [projectCount, setProjectCount] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!agencyId) {
      setProjectCount(0)
      setLoading(false)
      return
    }

    const fetchProjectCount = async () => {
      try {
        setLoading(true)
        const { count, error } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('agency_id', agencyId)

        if (error) throw error
        setProjectCount(count || 0)
      } catch (err) {
        console.error('Error fetching project count:', err)
        setProjectCount(0)
      } finally {
        setLoading(false)
      }
    }

    fetchProjectCount()
  }, [agencyId])

  return { projectCount, loading }
}

export interface AgencyUser {
  id: string
  email: string
  name: string
  role: string
}

export const useAgencyUsers = (agencyId: string | null | undefined) => {
  const [users, setUsers] = useState<AgencyUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!agencyId) {
      setUsers([])
      setLoading(false)
      return
    }

    const fetchAgencyUsers = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('users')
          .select('id, email, name, role')
          .eq('agency_id', agencyId)
          .in('role', ['admin', 'super_admin', 'manager'])
          .order('name', { ascending: true })

        if (error) throw error
        setUsers(data || [])
        setError(null)
      } catch (err) {
        console.error('Error fetching agency users:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch agency users')
        setUsers([])
      } finally {
        setLoading(false)
      }
    }

    fetchAgencyUsers()
  }, [agencyId])

  return { users, loading, error }
}
